import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useHistory } from 'react-router-dom';
import { Box, DropdownMenu, TableList, TableListActions, Typography, useOverlayContext, withBiContainer } from 'seagull';
import { Copy, Edit, Eye, Trash } from 'seagull/icons';
import { useStore } from '~/contexts';
import { useContactsMap } from '~/hooks/reactQuery/derived/useContactsMap';
import { useDuplicateDraft } from '~/hooks/reactQuery/mutations/useDuplicateDraft';
import { usePreviewDraft } from '~/hooks/usePreviewDraft';
import { useTerminateAgreement } from '~/hooks/useTerminateAgreement';
import { timeModel } from '~/models';
import { selectors } from '~/store/state-selectors';
import { ArchivedProposalsDashboardTestIds } from '../ArchivedProposalsDashboard';
import { getUICompanyName } from '../ArchivedProposalsDashboard.utils';
import { useArchivedProposals } from '../hooks/useArchivedProposals';
import { useUnarchiveDraft } from '../hooks/useUnarchiveDraft';
import styles from './ArchivedProposalRow.module.scss';
export const ArchivedProposalRow = withBiContainer((props)=>({
        name: 'archived_proposal_row',
        data: {
            draftId: props.draft.id
        }
    }))((props)=>{
    var _draft_lastModifiedBy, _draft_lastModifiedBy1;
    const worksAt = useStore(selectors.user.business);
    const { contactsMap, sampleContact } = useContactsMap(worksAt === null || worksAt === void 0 ? void 0 : worksAt.id);
    const { refresh } = useArchivedProposals();
    const { draft, index } = props;
    const { showConfirmAction } = useOverlayContext();
    const draftLabel = getUICompanyName(draft);
    const history = useHistory();
    const { duplicateDraft } = useDuplicateDraft({
        onSuccess: (newDraftId)=>history.push("/draft/".concat(newDraftId)),
        biCode: 18803
    });
    const { unarchiveDraft } = useUnarchiveDraft({
        onSuccess: ()=>history.push("/draft/".concat(draft.id)),
        biCode: 18802
    });
    const { previewDraft } = usePreviewDraft({
        sampleContact,
        contactsMap
    });
    const { terminateDraftById } = useTerminateAgreement();
    const draftSubLabel = draft.title;
    const handlePreviewDraft = ()=>{
        previewDraft(draft.id);
    };
    const showDuplicateConfirmModal = async ()=>{
        showConfirmAction({
            onConfirm: ()=>{
                duplicateDraft(draft.id);
            },
            title: 'Duplicate archived proposal',
            content: 'Please note: the new copy of this proposal will be saved in your Drafts folder.',
            primaryButtonLabel: 'Continue',
            secondaryButtonLabel: 'Cancel'
        });
    };
    const showEditConfirmModal = async ()=>{
        showConfirmAction({
            onConfirm: ()=>{
                unarchiveDraft(draft.id);
            },
            title: 'Edit archived proposal',
            content: 'Please note: editing an archived proposal will move it back to the Drafts folder.',
            primaryButtonLabel: 'Continue',
            secondaryButtonLabel: 'Cancel'
        });
    };
    return /*#__PURE__*/ _jsx(TableList.Row, {
        rowId: draft.id,
        testId: ArchivedProposalsDashboardTestIds.Row.Root(index),
        items: [
            {
                value: /*#__PURE__*/ _jsxs(Box, {
                    className: styles.companyName,
                    children: [
                        draftLabel && /*#__PURE__*/ _jsx(Typography, {
                            ellipsis: true,
                            weight: "normal",
                            testId: ArchivedProposalsDashboardTestIds.Row.CompanyName(index),
                            children: draftLabel
                        }),
                        /*#__PURE__*/ _jsxs(Typography, {
                            as: "span",
                            variant: "paragraph2",
                            skin: "secondary",
                            testId: ArchivedProposalsDashboardTestIds.Row.DraftName(index),
                            children: [
                                "(",
                                draftSubLabel || 'Unnamed Draft',
                                ")"
                            ]
                        })
                    ]
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    testId: ArchivedProposalsDashboardTestIds.Row.ArchivedBy(index),
                    children: "".concat((_draft_lastModifiedBy = draft.lastModifiedBy) === null || _draft_lastModifiedBy === void 0 ? void 0 : _draft_lastModifiedBy.firstName, " ").concat((_draft_lastModifiedBy1 = draft.lastModifiedBy) === null || _draft_lastModifiedBy1 === void 0 ? void 0 : _draft_lastModifiedBy1.lastName)
                })
            },
            {
                value: /*#__PURE__*/ _jsx(Typography, {
                    skin: "secondary",
                    testId: ArchivedProposalsDashboardTestIds.Row.ArchivedDate(index),
                    children: timeModel.formatTimeAgo(draft.lastModifiedOn)
                })
            }
        ],
        actionColumn: /*#__PURE__*/ _jsxs(TableListActions, {
            testId: ArchivedProposalsDashboardTestIds.Row.Actions.Root(index),
            children: [
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    onClick: handlePreviewDraft,
                    iconStart: /*#__PURE__*/ _jsx(Eye, {}),
                    testId: ArchivedProposalsDashboardTestIds.Row.Actions.Preview(index),
                    children: "Preview"
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    onClick: ()=>showEditConfirmModal(),
                    iconStart: /*#__PURE__*/ _jsx(Edit, {}),
                    testId: ArchivedProposalsDashboardTestIds.Row.Actions.Edit(index),
                    children: "Edit"
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    onClick: ()=>showDuplicateConfirmModal(),
                    iconStart: /*#__PURE__*/ _jsx(Copy, {}),
                    testId: ArchivedProposalsDashboardTestIds.Row.Actions.Duplicate(index),
                    children: "Duplicate"
                }),
                /*#__PURE__*/ _jsx(DropdownMenu.Item, {
                    onClick: (_e, context)=>terminateDraftById(draft.id, ()=>refresh(), context),
                    skin: "error",
                    iconStart: /*#__PURE__*/ _jsx(Trash, {}),
                    testId: ArchivedProposalsDashboardTestIds.Row.Actions.Delete(index),
                    children: "Delete"
                })
            ]
        })
    }, draft.id);
});
